// region Imports
import React, {useEffect, Suspense} from "react";
import {useState} from 'react'
import V56spinner from '../components/loaderIndicators/v56_spinner';
import {
    useRecoilState, useRecoilValue, useSetRecoilState,
} from 'recoil';
import {
    buckets_, activeBucket_, loadingText_, lastTimeFileAdded_
} from "../App";
import V56EmptyState from "../components/v56EmptyState";
import {v56a_getBuckets} from "../v56_api";
import StatsDisplay from "../components/statsDisplay";
// endregion

export default function V56CloudStorage() {

    // region States
    const [loading, setLoading] = useState(true);
    const [showEditBucket, setShowEditBucket] = useState(false);
    const [showBrowseBucket, setShowBrowseBucket] = useState(false);
    const [showEditAccess, setShowEditAccess] = useState(false);
    const [showInformation, setShowInformation] = useState(false);
    const [currFlyout, setCurrFlyout] = useState('');
    const [showNewBucket, setShowNewBucket] = useState(false);

    // Personal information
    const [buckets, setBuckets] = useRecoilState(buckets_);
    const activeBucket = useRecoilValue(activeBucket_);
    const setLoadingText = useSetRecoilState(loadingText_);

    // endregion

    const V56BucketList = React.lazy(() => import('../modules/v56_cloud_storage/v56BucketList'));
    const V56NewBucket = React.lazy(() => import('../components/newBucket'));
    const V56EditBucket = React.lazy(() => import('../components/editBucket'));
    const V56FileBrowser = React.lazy(() => import('../components/fileBrowser'));
    const V56EditAccess = React.lazy(() => import('../components/editAccess'));

    useEffect(() => {
        (async () => {
            await reloadBuckets();
        })();
    }, []);

    async function reloadBuckets() {
        try {
            // Get buckets
            //setLoadingText('Loading buckets..');
            const buckets__ = await v56a_getBuckets();
            if (buckets__.status === 200) {
                setBuckets(buckets__.data);
            }

            setLoading(false);

        } catch (e) {
            console.log(e);
            //window.location.href = 'https://secure.vault56.com';
        }
    }

    /**
     * Callback to show new bucket flyout V56NewBucket
     */
    function callBackNewBucket() {
        setCurrFlyout('newBucket');
        setShowNewBucket(true);
    }

    /**
     * Callback to show flyout V56EditBucket
     */
    function callBackEditBucket() {
        setCurrFlyout('editBucket');
        setShowEditBucket(true);
    }

    /**
     * Callback to show flyout V56FileBrowser
     */
    function callBackBrowseBucket() {
        setCurrFlyout('browseBucket');
        setShowBrowseBucket(true);
    }

    /**
     * Callback to reload bucket list
     */
    function callBackReloadBucketList() {
        (async () => {
            try {
                // Get buckets
                const buckets__ = await v56a_getBuckets();
                if (buckets__.status === 200) {
                    setBuckets(buckets__.data);
                }
            } catch (e) {
                console.log(e);
            }
        })();
    }


    /**
     * Callback to show flyout V56EditAccess
     */
    function callBackEditAccess() {
        setCurrFlyout('editAccess');
        setShowEditAccess(true);
    }

    /**
     * Callback to show flyout V56Information
     */
    function callBackInformation() {
        setCurrFlyout('Information');
        setShowInformation(true);
    }

    /**
     * Callback to close bucket information flyout in V56NewBucket
     * @param value
     */
    function setShowNewBucketCb(value) {
        setShowNewBucket(value);
        setCurrFlyout('');
    }

    /**
     * Callback to close bucket information flyout in V56EditBucket
     * @param value
     */
    function setShowEditBucketCb(value) {
        setShowEditBucket(value);
        setCurrFlyout('');
    }

    /**
     * Callback to close bucket browser flyout in V56FileBrowser
     * @param value
     */
    function setShowBrowseBucketCb(value) {
        setShowBrowseBucket(value);
        setCurrFlyout('');
    }

    /**
     * Callback to close bucket access flyout in V56EditAccess
     * @param value
     */
    function setShowEditAccessCb(value) {
        setShowEditAccess(value);
        setCurrFlyout('');
    }

    /**
     * Combines multiple class strings to one
     * @param classes
     * @returns {string}
     */
    function classNames(...classes) {
        return classes.filter(Boolean).join(' ')
    }

    /**
     * Renders main content
     * Depends on currPage
     * @type {(function(): (*|null))|*}
     */
    const renderContent = React.useCallback(() => {
                return <div className="mt-2">
                    <div className="mt-5"></div>
                    {buckets.length > 0 ?
                        renderBucketList():
                        <V56EmptyState
                            strText='Click here to add a new bucket'
                            callBack={callBackNewBucket}
                        >
                        </V56EmptyState>
                    }
                </div>
    }, [buckets]);

    /**
     * Renders side menu (flyout)
     * Depends on currFlyout
     * @type {(function(): (string|*))|*}
     */
    const renderFlyout = React.useCallback(() => {
        switch(currFlyout) {
            case '':
                return ''
            case 'newBucket':
                return <Suspense><V56NewBucket open={showNewBucket} setOpen={setShowNewBucketCb}/></Suspense>
            case 'editBucket':
                return <Suspense><V56EditBucket open={showEditBucket} setOpen={setShowEditBucketCb} bucket={activeBucket}/></Suspense>
            case 'browseBucket':
                return <Suspense><V56FileBrowser open={showBrowseBucket} setOpen={setShowBrowseBucketCb} bucket={activeBucket} callbackReload={callBackReloadBucketList}/></Suspense>
            case 'editAccess':
                return <Suspense><V56EditAccess open={showEditAccess} setOpen={setShowEditAccessCb} bucket={activeBucket}/></Suspense>

            default:
                return '';

        }
    }, [currFlyout]);

    const renderBucketList = React.useCallback(() => {
        if (buckets.length > 0) {
            return (
                <div className="mt-2">
                    <div className="flex mb-5">
                        <div className="w-full">
                            <StatsDisplay/>
                        </div>
                    </div>
                    <Suspense>
                        <V56BucketList
                            callBackEdit={callBackEditBucket}
                            callBackNew={callBackNewBucket}
                            callBackAccess={callBackEditAccess}
                            callBackBrowse={callBackBrowseBucket}
                            callbackReload={callBackReloadBucketList}
                        />
                    </Suspense>
                </div>
            )
        }
    }, [buckets]);

    // region Main return
    if (loading) {
        setLoadingText('Loading buckets...');
        return(<V56spinner/>);
    } else {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {renderContent()}
                {renderFlyout()}
            </div>
        )

    }
    // endregion
}
